"use client";

import type { ReactNode } from "react";
import { localizedText } from "@/lib/categories/localization";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, CircleSlash, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { descendantCount, flattenVisible } from "@/lib/categories/tree-model";
import type { LanguageMap } from "@/services/category.service";
import type { CategoryStatus, TreeLike } from "@/lib/categories/tree-model";

/**
 * What a row needs beyond id/name/children. Both are optional so a tree whose
 * nodes carry neither — the template tree has no enabled flag — renders without
 * empty space where the markers would be.
 */
export type TreeRowNode<T> = TreeLike<T> & {
  name: LanguageMap;
  /** Omitted entirely by trees that have no such concept. */
  isCanonical?: boolean;
};

const ROW_HEIGHT = 32;
const OVERSCAN = 6;

interface CategoryTreeProps<T extends TreeRowNode<T>> {
  statuses: ReadonlyMap<string, CategoryStatus>;
  nodes: T[];
  selectedId: string | null;
  expanded: ReadonlySet<string>;
  /** Categories holding unsaved edits, marked so nothing is silently pending. */
  draftIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onExpandBranch: (node: T) => void;
  onSelect: (node: T) => void;
  /**
   * The menu body for a row. Supplied as a render prop so each row owns its own
   * trigger, rather than the tree having to position a shared menu by hand.
   */
  renderRowMenu: (node: T) => ReactNode;
  /**
   * Windowing only pays off on large trees; below the threshold every row is
   * rendered so the browser's own find still works.
   */
  virtualize: boolean;
}

export function CategoryTree<T extends TreeRowNode<T>>({
  statuses,
  nodes,
  selectedId,
  expanded,
  draftIds,
  onToggle,
  onExpandBranch,
  onSelect,
  renderRowMenu,
  virtualize,
}: CategoryTreeProps<T>) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(480);

  const rows = useMemo(() => flattenVisible(nodes, expanded), [nodes, expanded]);
  const selectedIndex = rows.findIndex((r) => r.node.id === selectedId);

  // Measure once mounted and on resize, so the window is sized to the panel
  // rather than to a guess.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setViewportHeight(el.clientHeight || 480);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Keep the selected row reachable when selection moves by keyboard.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || selectedIndex < 0) return;
    if (!virtualize) {
      el.querySelector<HTMLElement>(`[data-id="${CSS.escape(selectedId ?? "")}"]`)
        ?.scrollIntoView({ block: "nearest" });
      return;
    }
    const top = selectedIndex * ROW_HEIGHT;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + ROW_HEIGHT > el.scrollTop + el.clientHeight) {
      el.scrollTop = top + ROW_HEIGHT - el.clientHeight;
    }
  }, [selectedIndex, selectedId, virtualize]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!virtualize) return;
      setScrollTop(e.currentTarget.scrollTop);
    },
    [virtualize],
  );

  const [start, end] = virtualize
    ? [
        Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN),
        Math.min(
          rows.length,
          Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN,
        ),
      ]
    : [0, rows.length];

  const move = (delta: number) => {
    const from = selectedIndex < 0 ? 0 : selectedIndex;
    const next = rows[Math.max(0, Math.min(rows.length - 1, from + delta))];
    if (next) onSelect(next.node);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const current = rows[selectedIndex]?.node;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        move(-1);
        break;
      case "Home":
        e.preventDefault();
        if (rows[0]) onSelect(rows[0].node);
        break;
      case "End":
        e.preventDefault();
        if (rows.length) onSelect(rows[rows.length - 1].node);
        break;
      case "ArrowRight":
        e.preventDefault();
        if (current?.children.length && !expanded.has(current.id)) {
          if (e.altKey) onExpandBranch(current);
          else onToggle(current.id);
        } else {
          move(1);
        }
        break;
      case "ArrowLeft": {
        e.preventDefault();
        if (!current) break;
        if (expanded.has(current.id)) {
          onToggle(current.id);
          break;
        }
        const depth = rows[selectedIndex].depth;
        const parent = rows
          .slice(0, selectedIndex)
          .reverse()
          .find((r) => r.depth < depth);
        if (parent) onSelect(parent.node);
        break;
      }
    }
  };

  return (
    <div
      ref={viewportRef}
      role="tree"
      aria-label="Categories"
      tabIndex={0}
      className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1.5 pb-3 outline-none"
      onScroll={handleScroll}
      onKeyDown={onKeyDown}
    >
      <div
        style={{
          height: virtualize ? rows.length * ROW_HEIGHT : undefined,
          position: "relative",
        }}
      >
        <div
          style={
            virtualize ? { transform: `translateY(${start * ROW_HEIGHT}px)` } : undefined
          }
        >
          {rows.slice(start, end).map(({ node, depth, pos, size }) => {
            const hasChildren = node.children.length > 0;
            const isOpen = expanded.has(node.id);
            const isSelected = node.id === selectedId;
            return (
              <div
                key={node.id}
                data-id={node.id}
                role="treeitem"
                aria-level={depth + 1}
                aria-posinset={pos}
                aria-setsize={size}
                aria-selected={isSelected}
                aria-expanded={hasChildren ? isOpen : undefined}
                title={localizedText(node.name)}
                style={{ paddingLeft: 8 + depth * 14, height: ROW_HEIGHT }}
                className={cn(
                  "group flex cursor-pointer select-none items-center gap-1 rounded-md pr-1.5 text-sm",
                  isSelected
                    ? "bg-pink-50 font-medium text-pink-700"
                    : "hover:bg-muted",
                )}
                onClick={() => onSelect(node)}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  aria-hidden={!hasChildren}
                  title={
                    hasChildren
                      ? "Show subcategories (hold Alt to expand all of them)"
                      : undefined
                  }
                  className={cn(
                    "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded",
                    "text-muted-foreground hover:bg-border hover:text-foreground",
                    !hasChildren && "invisible",
                    isSelected && "text-pink-700",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.altKey) onExpandBranch(node);
                    else onToggle(node.id);
                  }}
                >
                  <ChevronRight
                    className={cn(
                      "h-[15px] w-[15px] transition-transform",
                      isOpen && "rotate-90",
                    )}
                  />
                </button>

                <span
                  className={cn(
                    "flex-1 truncate",
                    statuses.get(node.id) !== "Enabled" &&
                      !isSelected &&
                      "text-muted-foreground",
                  )}
                >
                  {localizedText(node.name)}
                </span>

                {draftIds.has(node.id) && (
                  <span
                    title="You have unsaved changes here"
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-pink-600"
                  />
                )}
                {statuses.get(node.id) !== "Enabled" && (
                  <CircleSlash
                    className="h-3 w-3 shrink-0 text-amber-700"
                    aria-label={statuses.get(node.id)}
                  />
                )}
                {hasChildren && !isOpen && (
                  <span className="shrink-0 font-mono text-[10.5px] tabular-nums text-muted-foreground">
                    {descendantCount(node)}
                  </span>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={`Actions for ${localizedText(node.name)}`}
                      className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded text-muted-foreground opacity-50 hover:bg-border focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  {renderRowMenu(node)}
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
